import { Injectable, inject } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { LoadingService } from '../loading.service'

export interface LoadingToken {
  id: string
  message: string
  priority: 'low' | 'medium' | 'high'
  timestamp: number
}

export interface LoadingState {
  isLoading: boolean
  message: string
  isSilent?: boolean
}

/**
 * Token-based loading state management service
 *
 * Provides efficient concurrent loading operation tracking using unique tokens
 * to prevent race conditions and ensure proper loading state coordination.
 * Replaces legacy counter-based approach with modern token management.
 *
 * Key features:
 * - Token-based concurrent operation tracking
 * - Priority-based message display (high > medium > low)
 * - Race condition prevention through unique identifiers
 * - Reactive loading state streams with BehaviorSubject
 * - Debug support for active operation monitoring
 */
@Injectable({
  providedIn: 'root',
})
export class TokenizedMessageLoadingService {
  private readonly loadingService = inject(LoadingService)
  private activeTokens = new Map<string, LoadingToken>()
  private loading = new BehaviorSubject<LoadingState>({ isLoading: false, message: '' })

  loading$ = this.loading.asObservable()

  /**
   * Start loading with a unique token
   * Prevents race conditions by tracking individual loading operations
   *
   * @param message Loading message to display
   * @param tokenId Unique identifier for this loading operation
   * @param priority Priority level affects which message is displayed when multiple operations are active
   * @returns Token ID for stopping this specific loading operation
   */
  startLoading(message: string, tokenId?: string, priority: 'low' | 'medium' | 'high' = 'medium'): string {
    const id = tokenId || this.generateTokenId()

    const token: LoadingToken = {
      id,
      message,
      priority,
      timestamp: Date.now(),
    }

    this.activeTokens.set(id, token)
    this.updateLoadingState()

    console.log(
      `TokenizedMessageLoadingService: Started loading [${id}] - "${message}" (${this.activeTokens.size} active)`
    )

    return id
  }

  /**
   * Stop loading for a specific token
   * Only clears global loading state when all tokens are removed
   *
   * @param tokenId Token ID returned from startLoading()
   */
  stopLoading(tokenId: string): void {
    if (this.activeTokens.has(tokenId)) {
      const token = this.activeTokens.get(tokenId)
      this.activeTokens.delete(tokenId)
      this.updateLoadingState()
      console.log(
        `TokenizedMessageLoadingService: Stopped loading [${tokenId}] - "${token.message}" (${this.activeTokens.size} active)`
      )
    } else {
      console.warn(
        `TokenizedMessageLoadingService: Attempted to stop non-existent token: ${tokenId}`
      )
    }
  }

  /**
   * Get current active loading operations (for debugging)
   */
  getActiveTokens(): LoadingToken[] {
    return Array.from(this.activeTokens.values())
  }

  /**
   * Force clear all loading tokens (emergency cleanup)
   */
  clearAllTokens(): void {
    const clearedCount = this.activeTokens.size
    this.activeTokens.clear()
    this.updateLoadingState()
    console.log(`TokenizedMessageLoadingService: Force cleared ${clearedCount} loading tokens`)
  }

  private updateLoadingState(): void {
    const hasActiveTokens = this.activeTokens.size > 0

    if (hasActiveTokens) {
      const displayToken = this.selectDisplayToken()
      const state = {
        isLoading: true,
        message: displayToken.message,
      }
      this.loading.next(state)
      // Sync with legacy LoadingService for UI components
      // Show loading immediately so users see feedback right away
      this.loadingService.set(state)
    } else {
      const state = {
        isLoading: false,
        message: '',
      }
      this.loading.next(state)
      // Sync with legacy LoadingService for UI components
      // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError when clearing
      setTimeout(() => this.loadingService.set(state), 0)
    }
  }

  private selectDisplayToken(): LoadingToken {
    const tokens = Array.from(this.activeTokens.values())

    const priorityOrder = { high: 3, medium: 2, low: 1 }

    return tokens.sort((a, b) => {
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority]
      if (priorityDiff !== 0) return priorityDiff
      return b.timestamp - a.timestamp
    })[0]
  }

  private generateTokenId(): string {
    return `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}
